// src/components/dashboard/Workshop5Charts.js
import React from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    ScatterChart,
    Scatter,
    ZAxis
} from 'recharts';

// Colors for charts
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#FF6B6B', '#4ECDC4', '#45B7D1'];
const RISK_COLORS = {
    'Critique': '#DC2626',
    'Élevé': '#F59E0B',
    'Moyen': '#FBBF24',
    'Faible': '#10B981',
    'Négligeable': '#6B7280'
};

const Workshop5Charts = ({ data, rawStrategicScenarios = [] }) => {
    const { t } = useTranslation();
    console.log('🎯 Workshop5Charts received data:', data);
    console.log('🎯 Workshop5Charts received rawStrategicScenarios:', rawStrategicScenarios);

    // State for table pagination
    const [currentPage, setCurrentPage] = React.useState(0);
    const itemsPerPage = 5;

    // State for scenario details modal
    const [selectedScenario, setSelectedScenario] = React.useState(null);
    const [showModal, setShowModal] = React.useState(false);

    // State for tooltip
    const [tooltip, setTooltip] = React.useState({ visible: false, content: null, x: 0, y: 0 });

    const handleMouseEnter = (e, scenario) => {
        const rect = e.target.getBoundingClientRect();
        setTooltip({
            visible: true,
            content: scenario,
            x: rect.left + window.scrollX,
            y: rect.top + window.scrollY - 10
        });
    };

    const handleMouseLeave = () => {
        setTooltip({ visible: false, content: null, x: 0, y: 0 });
    };

    if (!data) {
        return (
            <div className="bg-white p-6 rounded-lg shadow-sm border">
                <p className="text-gray-500">{t('workshop5Charts.noData')}</p>
            </div>
        );
    }

    // Helper function to calculate risk from measures (matching ResidualRiskCalculatorV2 logic)
    const calculateRiskFromMeasures = (initialGravity, initialLikelihood, timelineData) => {
        const phases = ['immediate', 'shortTerm', 'mediumTerm'];

        // Get cumulative measures up to mediumTerm (T0+6)
        const cumulativeMeasures = [];
        phases.forEach(phase => {
            if (timelineData[phase]?.measures) {
                cumulativeMeasures.push(...timelineData[phase].measures);
            }
        });

        // Calculate max effectiveness
        let maxEffectiveness = 0;
        if (cumulativeMeasures.length > 0) {
            const effectivenessValues = cumulativeMeasures.map(m => {
                const eff = m.effectiveness || 50;
                return typeof eff === 'number' ? (eff > 1 ? eff : eff * 100) : 50;
            });
            maxEffectiveness = Math.max(...effectivenessValues);
        }

        // Reduction factor (0 to 0.9 max)
        const reductionFactor = Math.min(0.9, maxEffectiveness / 100);

        // Apply uniform reduction
        const newLikelihood = Math.max(1, Math.round(initialLikelihood * (1 - reductionFactor)));
        const newGravity = Math.max(1, Math.round(initialGravity * (1 - reductionFactor)));
        const newScore = newLikelihood * newGravity;

        return {
            likelihood: newLikelihood,
            gravity: newGravity,
            riskScore: newScore
        };
    };

    const getScenarioRiskData = (scenario) => {
        // 1. Calculate Likelihood
        let likelihood = scenario.likelihood;
        if (!likelihood && scenario.operationalScenarios && scenario.operationalScenarios.length > 0) {
            const avgVraisemblance = scenario.operationalScenarios.reduce((sum, op) =>
                sum + (op.scenarioVraisemblance || op.vraisemblance || 2), 0) / scenario.operationalScenarios.length;
            likelihood = Math.round(avgVraisemblance);
        } else if (!likelihood) {
            likelihood = 2; // Default
        }

        const gravity = scenario.gravity || 3;

        // 2. Initial Risk = Gravity * Likelihood
        const initialRisk = gravity * likelihood;

        // 3. Residual Risk
        let residualRisk;

        // Try from temporalEvolution (mediumTerm / T0+6)
        if (scenario.temporalEvolution?.timelineData) {
            const mediumTerm = scenario.temporalEvolution.timelineData.mediumTerm;
            residualRisk = mediumTerm?.riskScore || mediumTerm?.riskLevel;

            // If missing, calculate from measures
            if (!residualRisk) {
                const calculated = calculateRiskFromMeasures(gravity, likelihood, scenario.temporalEvolution.timelineData);
                residualRisk = calculated.riskScore;
            }
        }

        // Fallback if still no residual risk
        if (!residualRisk) {
            // Try legacy structure
            if (scenario.residualRisk?.residualRisk?.riskScore) {
                residualRisk = scenario.residualRisk.residualRisk.riskScore;
            } else {
                residualRisk = initialRisk; // No reduction
            }
        }

        // 4. Reduction
        let riskReduction = 0;
        if (initialRisk > 0) {
            riskReduction = Math.round(((initialRisk - residualRisk) / initialRisk) * 100);
        } else if (scenario.residualRisk?.riskReduction?.overallReduction) {
            // Fallback to legacy pre-calculated
            riskReduction = scenario.residualRisk.riskReduction.overallReduction;
        }

        return {
            initialRisk,
            residualRisk,
            riskReduction,
            likelihood,
            gravity
        };
    };

    // Process the actual data structure from the backend API
    const processWorkshop5Data = (backendData, scenarios = []) => {
        if (!backendData) {
            return {
                riskTreatmentData: [],
                riskMatrixData: [],
                totalScenarios: 0,
                scenariosWithTreatment: 0
            };
        }

        // Process Risk Treatment Options from backend structure
        const riskTreatmentData = [];
        if (backendData.riskTreatmentStats && backendData.riskTreatmentStats.byOption) {
            const optionLabels = {
                'accept': t('workshop5Charts.treatmentAccept'),
                'avoid': t('workshop5Charts.treatmentAvoid'),
                'transfer': t('workshop5Charts.treatmentTransfer'),
                'reduce': t('workshop5Charts.treatmentReduce'),
                'mitigate': t('workshop5Charts.treatmentMitigate'),
                'Non défini': t('workshop5Charts.undefined')
            };

            Object.entries(backendData.riskTreatmentStats.byOption).forEach(([option, count]) => {
                const label = optionLabels[option] || option;
                riskTreatmentData.push({ name: label, value: count });
            });
        }

        // Create risk matrix data from scenarios
        const riskMatrixData = scenarios.map((scenario, index) => {
            const { initialRisk, residualRisk, riskReduction, likelihood, gravity } = getScenarioRiskData(scenario);

            return {
                name: scenario.name || t('workshop5Charts.scenarioNumber', { number: index + 1 }),
                x: likelihood,
                y: gravity,
                z: residualRisk * 20, // bubble size
                initialRisk,
                residualRisk,
                riskReduction
            };
        }).filter(item => item.residualRisk > 0 && item.x > 0 && item.y > 0);

        return {
            riskTreatmentData,
            riskMatrixData,
            totalScenarios: backendData.strategicScenariosStats?.total || 0,
            scenariosWithTreatment: backendData.strategicScenariosStats?.withRiskTreatment || 0
        };
    };

    const processedData = processWorkshop5Data(data, rawStrategicScenarios);
    const {
        riskTreatmentData,
        riskMatrixData,
        totalScenarios,
        scenariosWithTreatment
    } = processedData;

    // Enhanced Custom Tooltip for Scatter Plot with fixed positioning
    const EnhancedScatterTooltip = ({ active, payload, coordinate }) => {
        if (active && payload && payload.length && coordinate) {
            const data = payload[0].payload;
            return (
                <div
                    className="bg-white p-4 border border-gray-200 rounded-lg shadow-xl max-w-sm z-50 pointer-events-none"
                    style={{
                        position: 'fixed',
                        left: coordinate.x + 10,
                        top: coordinate.y - 10,
                        transform: 'translate(0, -100%)'
                    }}
                >
                    <div className="flex items-center mb-3">
                        <div className={`w-3 h-3 rounded-full mr-2 ${data.residualRisk >= 16 ? 'bg-red-600' :
                            data.residualRisk >= 12 ? 'bg-red-500' :
                                data.residualRisk >= 6 ? 'bg-yellow-500' : 'bg-green-500'
                            }`}></div>
                        <p className="font-bold text-gray-800">{data.name}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="space-y-2">
                            <div>
                                <span className="text-gray-600">{t('workshop5Charts.likelihood')}:</span>
                                <div className="font-semibold text-blue-600">{data.x}/5</div>
                            </div>
                            <div>
                                <span className="text-gray-600">{t('workshop5Charts.impact')}:</span>
                                <div className="font-semibold text-purple-600">{data.y}/5</div>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <div>
                                <span className="text-gray-600">{t('workshop5Charts.initialRiskLabel')}:</span>
                                <div className="font-semibold text-red-600">{data.initialRisk.toFixed(1)}</div>
                            </div>
                            <div>
                                <span className="text-gray-600">{t('workshop5Charts.residualRiskLabel')}:</span>
                                <div className="font-semibold text-orange-600">{data.residualRisk.toFixed(1)}</div>
                            </div>
                        </div>
                    </div>
                    {data.riskReduction > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="flex items-center justify-between">
                                <span className="text-gray-600 text-sm">{t('workshop5Charts.reduction')}</span>
                                <div className="flex items-center">
                                    <svg className="w-4 h-4 text-green-500 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5-5 5M6 12h12" />
                                    </svg>
                                    <span className="font-bold text-green-600">-{data.riskReduction.toFixed(1)}%</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            );
        }
        return null;
    };

    // Scenario Details Modal Component
    const ScenarioDetailsModal = ({ scenario, isOpen, onClose }) => {
        if (!isOpen || !scenario) return null;

        const { initialRisk, residualRisk, riskReduction, likelihood, gravity } = getScenarioRiskData(scenario);
        const treatment = scenario.riskTreatment?.option || t('workshop5Charts.undefined');

        const treatmentLabels = {
            'accept': t('workshop5Charts.treatmentAccept'),
            'avoid': t('workshop5Charts.treatmentAvoid'),
            'transfer': t('workshop5Charts.treatmentTransfer'),
            'reduce': t('workshop5Charts.treatmentReduce'),
            'mitigate': t('workshop5Charts.treatmentMitigate')
        };

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <h2 className="text-2xl font-bold text-gray-800">{t('workshop5Charts.scenarioDetails')}</h2>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
                            >
                                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div className="p-6 space-y-6">
                        {/* Scenario Name */}
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-200">
                            <h3 className="text-lg font-semibold text-blue-800 mb-2">{t('workshop5Charts.scenarioName')}</h3>
                            <p className="text-gray-800">{scenario.name || t('workshop5Charts.unnamedScenario')}</p>
                        </div>

                        {/* Risk Metrics */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-red-50 p-4 rounded-xl border border-red-200">
                                <h4 className="text-sm font-semibold text-red-800 mb-2">{t('workshop5Charts.initialRisk')}</h4>
                                <div className="text-3xl font-bold text-red-600">{initialRisk.toFixed(1)}</div>
                            </div>
                            <div className="bg-orange-50 p-4 rounded-xl border border-orange-200">
                                <h4 className="text-sm font-semibold text-orange-800 mb-2">{t('workshop5Charts.residualRisk')}</h4>
                                <div className="text-3xl font-bold text-orange-600">{residualRisk.toFixed(1)}</div>
                            </div>
                        </div>

                        {/* Risk Reduction */}
                        {riskReduction > 0 && (
                            <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                                <h4 className="text-sm font-semibold text-green-800 mb-2">{t('workshop5Charts.riskReduction')}</h4>
                                <div className="flex items-center">
                                    <svg className="w-6 h-6 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5-5 5M6 12h12" />
                                    </svg>
                                    <span className="text-2xl font-bold text-green-600">-{riskReduction.toFixed(1)}%</span>
                                </div>
                            </div>
                        )}

                        {/* Treatment Strategy */}
                        <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
                            <h4 className="text-sm font-semibold text-purple-800 mb-2">{t('workshop5Charts.treatmentStrategy')}</h4>
                            <div className="text-lg font-semibold text-purple-600">
                                {treatmentLabels[treatment] || treatment}
                            </div>
                        </div>

                        {/* Additional Details */}
                        {scenario.description && (
                            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                                <h4 className="text-sm font-semibold text-gray-800 mb-2">{t('workshop5Charts.description')}</h4>
                                <p className="text-gray-700">{scenario.description}</p>
                            </div>
                        )}

                        {/* Risk Components */}
                        {(likelihood || gravity) && (
                            <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-200">
                                <h4 className="text-sm font-semibold text-indigo-800 mb-3">{t('workshop5Charts.riskComponents')}</h4>
                                <div className="grid grid-cols-2 gap-4">
                                    {likelihood && (
                                        <div>
                                            <span className="text-sm text-indigo-600">{t('workshop5Charts.likelihood')}:</span>
                                            <div className="text-xl font-bold text-indigo-800">
                                                {likelihood}/5
                                            </div>
                                        </div>
                                    )}
                                    {gravity && (
                                        <div>
                                            <span className="text-sm text-indigo-600">{t('workshop5Charts.gravity')}:</span>
                                            <div className="text-xl font-bold text-indigo-800">
                                                {scenario.residualRisk.initialRisk.gravity}/5
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-8">
            {/* Enhanced Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="group bg-gradient-to-br from-blue-50 to-indigo-100 p-6 rounded-xl border border-blue-200 hover:shadow-lg hover:scale-105 transition-all duration-300 cursor-pointer">
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="text-sm font-medium text-blue-800 mb-1">Total Scénarios</h4>
                            <p className="text-3xl font-bold text-blue-900 group-hover:text-blue-700 transition-colors">
                                {totalScenarios}
                            </p>
                            <p className="text-xs text-blue-600 mt-1">{t('workshop5Charts.scenariosAnalyzed')}</p>
                        </div>
                        <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center group-hover:bg-blue-600 transition-colors shadow-lg">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                        </div>
                    </div>
                </div>

                <div className="group bg-gradient-to-br from-purple-50 to-violet-100 p-6 rounded-xl border border-purple-200 hover:shadow-lg hover:scale-105 transition-all duration-300 cursor-pointer">
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="text-sm font-medium text-purple-800 mb-1">{t('workshop5Charts.withTreatment')}</h4>
                            <p className="text-3xl font-bold text-purple-900 group-hover:text-purple-700 transition-colors">
                                {scenariosWithTreatment}
                            </p>
                            <p className="text-xs text-purple-600 mt-1">
                                {totalScenarios > 0 ? t('workshop5Charts.treatedPercentage', { percentage: ((scenariosWithTreatment / totalScenarios) * 100).toFixed(1) }) : t('workshop5Charts.treatedPercentageZero')}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center group-hover:bg-purple-600 transition-colors shadow-lg">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                    </div>
                </div>
            </div>



            {/* Enhanced Risk Heat Map Table */}
            <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-500 group">
                <div className="flex items-center mb-8">
                    <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-gray-800 group-hover:text-gray-700 transition-colors">
                            {t('workshop5Charts.riskDashboard')}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">{t('workshop5Charts.detailedView')}</p>
                    </div>
                </div>

                {rawStrategicScenarios.length > 0 ? (
                    (() => {
                        const totalPages = Math.ceil(rawStrategicScenarios.length / itemsPerPage);
                        const startIndex = currentPage * itemsPerPage;
                        const endIndex = startIndex + itemsPerPage;
                        const currentScenarios = rawStrategicScenarios.slice(startIndex, endIndex);

                        const nextPage = () => {
                            setCurrentPage((prev) => (prev + 1) % totalPages);
                        };

                        const prevPage = () => {
                            setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
                        };

                        const goToPage = (pageIndex) => {
                            setCurrentPage(pageIndex);
                        };

                        return (
                            <div className="space-y-6">
                                {/* Pagination Info */}
                                {rawStrategicScenarios.length > itemsPerPage && (
                                    <div className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-xl border border-blue-200">
                                        <div className="flex items-center space-x-4">
                                            <div className="flex items-center">
                                                <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                                                <span className="text-gray-700 font-medium text-sm">
                                                    {t('workshop5Charts.pageOf', { current: currentPage + 1, total: totalPages })}
                                                </span>
                                            </div>
                                            <div className="text-gray-600 text-sm">
                                                {t('workshop5Charts.showingRange', { start: startIndex + 1, end: Math.min(endIndex, rawStrategicScenarios.length), total: rawStrategicScenarios.length })}
                                            </div>
                                        </div>

                                        {/* Navigation Controls */}
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={prevPage}
                                                disabled={totalPages <= 1}
                                                className="p-2 rounded-lg bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-md"
                                            >
                                                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                                </svg>
                                            </button>

                                            {/* Page Numbers */}
                                            <div className="flex space-x-1">
                                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                    let pageIndex;
                                                    if (totalPages <= 5) {
                                                        pageIndex = i;
                                                    } else if (currentPage < 3) {
                                                        pageIndex = i;
                                                    } else if (currentPage >= totalPages - 2) {
                                                        pageIndex = totalPages - 5 + i;
                                                    } else {
                                                        pageIndex = currentPage - 2 + i;
                                                    }

                                                    return (
                                                        <button
                                                            key={pageIndex}
                                                            onClick={() => goToPage(pageIndex)}
                                                            className={`w-8 h-8 rounded-lg text-sm font-medium transition-all duration-200 ${currentPage === pageIndex
                                                                ? 'bg-blue-500 text-white shadow-md'
                                                                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:shadow-md'
                                                                }`}
                                                        >
                                                            {pageIndex + 1}
                                                        </button>
                                                    );
                                                })}
                                            </div>

                                            <button
                                                onClick={nextPage}
                                                disabled={totalPages <= 1}
                                                className="p-2 rounded-lg bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-md"
                                            >
                                                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Table */}
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="bg-gray-50 border-b-2 border-gray-200">
                                                <th className="text-left p-4 text-sm font-semibold text-gray-700">{t('workshop5Charts.scenario')}</th>
                                                <th className="text-center p-4 text-sm font-semibold text-gray-700">{t('workshop5Charts.initialRisk')}</th>
                                                <th className="text-center p-4 text-sm font-semibold text-gray-700">{t('workshop5Charts.residualRisk')}</th>
                                                <th className="text-center p-4 text-sm font-semibold text-gray-700">{t('workshop5Charts.reduction')}</th>
                                                <th className="text-center p-4 text-sm font-semibold text-gray-700">{t('workshop5Charts.treatment')}</th>
                                                <th className="text-center p-4 text-sm font-semibold text-gray-700">{t('workshop5Charts.details')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentScenarios.map((scenario, index) => {
                                                const globalIndex = startIndex + index;
                                                const { initialRisk, residualRisk, riskReduction } = getScenarioRiskData(scenario);
                                                const treatment = scenario.riskTreatment?.option || t('workshop5Charts.undefined');

                                                const getRiskColor = (risk) => {
                                                    if (risk >= 16) return 'bg-red-600 text-white';
                                                    if (risk >= 12) return 'bg-red-500 text-white';
                                                    if (risk >= 6) return 'bg-yellow-500 text-white';
                                                    if (risk >= 1) return 'bg-green-500 text-white';
                                                    return 'bg-gray-300 text-gray-600';
                                                };

                                                const treatmentLabels = {
                                                    'accept': t('workshop5Charts.treatmentAccept'),
                                                    'avoid': t('workshop5Charts.treatmentAvoid'),
                                                    'transfer': t('workshop5Charts.treatmentTransfer'),
                                                    'reduce': t('workshop5Charts.treatmentReduce'),
                                                    'mitigate': t('workshop5Charts.treatmentMitigate')
                                                };

                                                return (
                                                    <tr key={globalIndex} className="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200 group">
                                                        <td className="p-4">
                                                            <div className="flex items-center">
                                                                <div className="w-2 h-8 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full mr-3 group-hover:from-blue-500 group-hover:to-blue-700 transition-colors"></div>
                                                                <div className="flex-1">
                                                                    <div className="font-medium text-gray-800 max-w-xs truncate" title={scenario.name}>
                                                                        {scenario.name || t('workshop5Charts.scenarioNumber', { number: globalIndex + 1 })}
                                                                    </div>
                                                                    <div className="text-xs text-gray-500 mt-1">
                                                                        {t('workshop5Charts.scenarioHash', { number: globalIndex + 1 })}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="p-4 text-center">
                                                            <div className={`w-12 h-12 rounded-xl mx-auto ${getRiskColor(initialRisk)} flex items-center justify-center text-sm font-bold shadow-md hover:shadow-lg transition-shadow duration-200`}>
                                                                {initialRisk.toFixed(0)}
                                                            </div>
                                                        </td>
                                                        <td className="p-4 text-center">
                                                            <div className={`w-12 h-12 rounded-xl mx-auto ${getRiskColor(residualRisk)} flex items-center justify-center text-sm font-bold shadow-md hover:shadow-lg transition-shadow duration-200`}>
                                                                {residualRisk.toFixed(0)}
                                                            </div>
                                                        </td>
                                                        <td className="p-4 text-center">
                                                            <div className="flex items-center justify-center">
                                                                {riskReduction > 0 ? (
                                                                    <div className="flex items-center bg-green-100 px-3 py-2 rounded-full">
                                                                        <svg className="w-4 h-4 text-green-600 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5-5 5M6 12h12" />
                                                                        </svg>
                                                                        <span className="text-sm font-semibold text-green-800">
                                                                            -{riskReduction.toFixed(1)}%
                                                                        </span>
                                                                    </div>
                                                                ) : (
                                                                    <span className="px-3 py-2 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                                                        0%
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="p-4 text-center">
                                                            <span className={`px-4 py-2 rounded-full text-xs font-semibold ${treatment !== 'Non défini'
                                                                ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                                                                : 'bg-gray-100 text-gray-600'
                                                                } transition-colors duration-200`}>
                                                                {treatmentLabels[treatment] || treatment}
                                                            </span>
                                                        </td>
                                                        <td className="p-4 text-center">
                                                            <div
                                                                onMouseEnter={(e) => handleMouseEnter(e, scenario)}
                                                                onMouseLeave={handleMouseLeave}
                                                                className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-full transition-all duration-200 hover:scale-110 group cursor-pointer"
                                                                title={t('workshop5Charts.viewDetails')}
                                                            >
                                                                <svg className="w-4 h-4 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                </svg>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Bottom Pagination (only show if more than one page) */}
                                {totalPages > 1 && (
                                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                                        <div className="flex items-center text-sm text-gray-600">
                                            <span>{t('workshop5Charts.totalScenarios', { count: rawStrategicScenarios.length })}</span>
                                        </div>

                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={prevPage}
                                                disabled={currentPage === 0}
                                                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                            >
                                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                                </svg>
                                                {t('workshop5Charts.previous')}
                                            </button>

                                            <div className="flex space-x-1">
                                                {Array.from({ length: totalPages }, (_, i) => (
                                                    <button
                                                        key={i}
                                                        onClick={() => goToPage(i)}
                                                        className={`w-10 h-10 rounded-lg text-sm font-medium transition-all duration-200 ${currentPage === i
                                                            ? 'bg-blue-500 text-white shadow-md transform scale-105'
                                                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:shadow-md hover:scale-105'
                                                            }`}
                                                    >
                                                        {i + 1}
                                                    </button>
                                                ))}
                                            </div>

                                            <button
                                                onClick={nextPage}
                                                disabled={currentPage === totalPages - 1}
                                                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                            >
                                                {t('workshop5Charts.next')}
                                                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })()
                ) : (
                    <div className="text-center py-12">
                        <div className="text-gray-400 mb-4">
                            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                            </svg>
                        </div>
                        <p className="text-gray-500 text-lg">{t('workshop5Charts.noScenarioData')}</p>
                    </div>
                )}
            </div>

            {tooltip.visible && ReactDOM.createPortal(
                <div
                    className="bg-white p-4 border border-gray-200 rounded-lg shadow-xl max-w-sm z-50 pointer-events-none"
                    style={{
                        position: 'absolute',
                        left: tooltip.x,
                        top: tooltip.y,
                        transform: 'translateY(-100%)'
                    }}
                >
                    <p className="font-bold text-gray-800">{tooltip.content.name}</p>
                    <p className="text-sm text-gray-600">{t('workshop5Charts.initialRiskTooltip', { value: getScenarioRiskData(tooltip.content).initialRisk.toFixed(1) })}</p>
                    <p className="text-sm text-gray-600">{t('workshop5Charts.residualRiskTooltip', { value: getScenarioRiskData(tooltip.content).residualRisk.toFixed(1) })}</p>
                </div>,
                document.body
            )}
        </div>
    );
};

export default Workshop5Charts;